import {
    Conditions, DATE, Filter, NestedFilterObject, NUMBER, SearchFilters,
} from '../store/common/search/types';
import commonSearchQueryBuilder from './search/commonSearchQueryBuilder';
import { removeItemFromArray } from '../util/array/arrayUtil';
import { tl } from './i18nService';
import { TabData, TabItem } from '../types/TabTypes';
import { isNullOrUndefined } from '../util/utils';
import { CUSTOM_QUERY_KEY } from '../store/common/constants';
import { mergeElasticsearchQueries } from '../util/elasticsearch/elasticsearchUtil';

export interface AppSpecifiedFilterService {
    getNestedPropertyPath?: (dataType: string) => string;
    getFilters: (dataType: string) => any[];
    addInternalFilters: (filters: SearchFilters, dataType?: string, implEvaluatorIds?: boolean) => SearchFilters;
    addAdditionalNestedField: (nestedFilter: NestedFilterObject, filter: Filter, additionalContext: any) => NestedFilterObject;
}
let appSpecifiedFilterService: AppSpecifiedFilterService;

class FilterValue {
    value: any = null;
    condition: Conditions;
    setValue(value: any) {
        this.condition = Conditions.EQUALS;
        this.value = value;
    }

    setLowerThan(value: any) {
        this.condition = Conditions.LOWER_THAN;
        this.value = {
            lt: value,
        };
    }

    setGreaterThan(value: any) {
        this.condition = Conditions.GREATER_THAN;
        this.value = {
            gt: value,
        };
    }

    setValueBetween(gt: any, lt: any) {
        this.condition = Conditions.BETWEEN;
        this.value = {
            gt,
            lt,
        };
    }

    isEmpty() {
        let empty = false;
        if (Array.isArray(this.value) && this.value.length === 0) {
            empty = true;
        } else if (this.condition !== Conditions.EQUALS) {
            Object.keys(this.value).forEach((key: string) => {
                if (this.value[key] === '' || this.value[key] === undefined) {
                    empty = true;
                }
            });
        } else if (this.value === '' || this.value === undefined || this.value === null) {
            empty = true;
        }
        return empty;
    }

    getValue(): any {
        return this.value;
    }
}

const commonFilteringService = {
    booleanQueryOperators: ['WHERE', 'MUST', 'SHOULD'] as any,
    // newFilter is the new evaluatorId filter to be added as a $nested object.
    appendFilterToEachNestedSet(query: any, newFilter: any, includedFields: string[]) {
        Object.keys(query).forEach((key: string) => {
            if (includedFields.includes(query[key].$path)) {
                query[key] = { ...query[key], ...newFilter };
            } else if (typeof query[key] === 'object' && !Array.isArray(query[key])) {
                this.appendFilterToEachNestedSet(query[key], newFilter, includedFields);
            }
        });
    },
    getFiltersWithSamePropertyValueReducerFunction(dataType: string) {
        const filterDefinitions: any[] = appSpecifiedFilterService.getFilters(dataType);
        const generateCustomField = (value: any) => {
            if (isNullOrUndefined(value)) {
                return [];
            }

            if (Array.isArray(value)) {
                return value;
            }

            return [value];
        };

        return (acc: Filter[], nextFilter: Filter): any[] => {
            const currDefinition = filterDefinitions.find((definition) => definition.name === nextFilter.name);
            const prevIndex = acc.findIndex((filter: Filter) => {
                const prevDefinition = filterDefinitions.find((definition) => definition.name === filter.name);
                return !(prevDefinition.customFilter || currDefinition.customFilter) && prevDefinition.value === currDefinition.value;
            });
            if (prevIndex > -1) {
                acc[prevIndex] = {
                    ...acc[prevIndex],
                    value: [...generateCustomField(acc[prevIndex].value), ...generateCustomField(nextFilter.value)],
                };
            } else {
                acc.push(nextFilter);
            }
            return acc;
        };
    },

    composeFilterWhenValueHasMissingOrExists(filter: Filter, boolQueryType: string, definition: { [key: string]: any }, selectedValue: string) {
        /*  Due to the backend does not support $missing/$exists in an array as multiple input query value, so has to do some tricky thing,
                        will remove it after backend support,
                        for example explanationStatus query could be ['$missing', 'DRAFT', ....] which does not support
                    */
        const removeSpecificValueFromArray = (specificValue: string) => {
            return {
                [boolQueryType]: {
                    should: {
                        [definition.value]: removeItemFromArray(filter.value, specificValue),
                        should: {
                            [definition.value]: specificValue,
                        },
                    },
                },
            };
        };

        if (Array.isArray(filter.value)) {
            if (filter.value.includes('$missing')) {
                return removeSpecificValueFromArray('$missing');
            }

            if (filter.value.includes('$exists')) {
                return removeSpecificValueFromArray('$exists');
            }
        }

        return { [definition.value]: selectedValue };
    },

    getLiveDataFilters(tab: TabData, additionalContext?: any): SearchFilters {
        const filterDefinitions: any[] = appSpecifiedFilterService.getFilters(tab.dataType);
        const boolQueryType = tab.booleanQueryOperator ? tab.booleanQueryOperator.toLowerCase() : 'must';
        const tabFilters = tab.filters
            .filter((filter: Filter) => !filter.tmp)
            .reduce(commonFilteringService.getFiltersWithSamePropertyValueReducerFunction(tab.dataType), [])
            .reduce(
                (acc: any, filter: Filter) => {
                    const definition = filterDefinitions.find((definition) => definition.name === filter.name);
                    const finalValue: FilterValue = new FilterValue();
                    if (filter.lowerThan !== undefined) {
                        finalValue.setLowerThan(filter.lowerThan);
                    } else if (filter.greaterThan !== undefined) {
                        finalValue.setGreaterThan(filter.greaterThan);
                    } else if ((definition.type === DATE || definition.type === NUMBER) && Array.isArray(filter.value)) {
                        finalValue.setValueBetween(filter.value[0], filter.value[1]);
                    } else if (definition.type === DATE && filter.value) {
                        const date1 = filter.value;
                        const date2 = `${filter.value.split('T')[0]}T23:59:59`;
                        finalValue.setValueBetween(date1, date2);
                    } else {
                        finalValue.setValue(filter.value);
                    }
                    if (finalValue.isEmpty()) {
                        return acc;
                    }
                    const value = finalValue.getValue();
                    const plainFilter = this.composeFilterWhenValueHasMissingOrExists(filter, boolQueryType, definition, value);
                    const isNested = this.isNested(definition);
                    let accWithAdditionalFilters = null;

                    // if custom filter then we ignore everything else
                    if (definition.customFilter) {
                        const { customQuery } = definition.items.find((item: any) => item.value === filter.value) || {};
                        return {
                            ...acc,
                            customQuery: {
                                [boolQueryType]: { ...acc?.customQuery, ...customQuery },
                            },
                        };
                        // If not custom filter then we check for additional filters
                        // eslint-disable-next-line no-else-return
                    } else {
                        if (definition.additionalFilters && definition.additionalFilters.length > 0) {
                            const additionalFilter = definition.additionalFilters[0];
                            const additional = filterDefinitions.find((definition) => definition.name === additionalFilter);
                            if (this.isNested(additional)) {
                                const nestedFilter = commonSearchQueryBuilder.createNestedFilter(additional.value, value);
                                const appendAdditionalFilterRecursively = (query: any): any => {
                                    if (query.should) {
                                        if (query.should.$nested) {
                                            if (query.should.$nested.$path === nestedFilter.$path) {
                                                const mergeWithPrev = { ...query.should.$nested, ...nestedFilter };
                                                const keys: any[] = Object.keys(mergeWithPrev).filter((key: string) => key !== '$path');
                                                const newFilter = {
                                                    $path: mergeWithPrev.$path,
                                                    should: keys.reduce((acc: any, k: string) => ({ ...acc, [k]: mergeWithPrev[k] }), {}),
                                                };
                                                return { ...query, should: { ...query.should, $nested: newFilter } };
                                            }
                                            return { ...query, should: appendAdditionalFilterRecursively(query.should) };
                                        }
                                        return { ...query, should: { ...query.should, $nested: nestedFilter } };
                                    }
                                    return { ...query, should: { $nested: nestedFilter } };
                                };
                                accWithAdditionalFilters = appendAdditionalFilterRecursively(acc);
                            }
                        }

                        if (isNested) {
                            const operatorIsShould: boolean = boolQueryType === 'should';
                            let nestedFilter = commonSearchQueryBuilder.createNestedFilter(definition.value, value);
                            // special cases like workflow tabs
                            if (filter.additionalNestedField && additionalContext) {
                                nestedFilter = appSpecifiedFilterService.addAdditionalNestedField(nestedFilter, filter, additionalContext);
                            }

                            // backend does not support $missing/$exists in an array as multiple input query value
                            if (Array.isArray(filter.value)
                                && (filter.value.includes('$missing') || filter.value.includes('$exists'))) {
                                nestedFilter = {
                                    $path: nestedFilter.$path,
                                    ...plainFilter,
                                };
                            }

                            let initialNestedFilterAdded = false;
                            let newAcc = { ...(accWithAdditionalFilters || acc) };
                            const filterUpdater = (query: any): any => {
                                const keys: string[] = Object.keys(query);

                                for (const key of keys) {
                                    if (initialNestedFilterAdded || key === CUSTOM_QUERY_KEY) {
                                        break;
                                    }

                                    if (query[key].$path === nestedFilter.$path) {
                                        initialNestedFilterAdded = true;
                                        if (operatorIsShould) {
                                            const merge = (filter: any) => (acc: any, next: string) => {
                                                if (next !== '$path') {
                                                    return { ...acc, [next]: filter[next] };
                                                }
                                                return acc;
                                            };
                                            const queryRef = query[key].should ? query[key].should : query[key];
                                            const newQuery: any = {
                                                ...Object.keys(queryRef).reduce(merge(queryRef), {}),
                                                ...Object.keys(nestedFilter).reduce(merge(nestedFilter), {}),
                                            };
                                            return {
                                                ...query,
                                                [key]: {
                                                    $path: query[key].$path,
                                                    should: newQuery,
                                                },
                                            };
                                        }
                                        return {
                                            ...query,
                                            [key]: { ...query[key], ...nestedFilter },
                                        };
                                    }
                                    if (key !== '$nested' && typeof query[key] === 'object' && !Array.isArray(query[key])) {
                                        const result = filterUpdater(query[key]);
                                        if (result) {
                                            return { ...query, [key]: { ...query[key], ...result } };
                                        }
                                    }
                                }
                                return null;
                            };

                            newAcc = { ...newAcc, ...filterUpdater(newAcc) };

                            /* Two cases there where this block will apply.
                                1 - not initialNestedFilterAdded and [booleanQueryOperator] key is empty!
                                2 - not initialNestedFilterAdded and [booleanQueryOperator] key is filled! so $PATH is different in two $nested.
                             */
                            if (!initialNestedFilterAdded) {
                                // $nested is only necessary when another nested.
                                if (!newAcc.$nested && !newAcc.should) {
                                    if (operatorIsShould) {
                                        newAcc = { ...newAcc, should: { $nested: nestedFilter } };
                                    } else {
                                        newAcc = { ...newAcc, $nested: nestedFilter };
                                    }
                                } else {
                                    const appendFilter = (query: any, newFilter: any): any => {
                                        if (query.must === undefined) {
                                            return { ...query, ...newFilter };
                                        }
                                        return { ...query, ...appendFilter(query.must, newFilter) };
                                    };
                                    const $newNestedFilter = { must: { $nested: nestedFilter } };
                                    if (operatorIsShould && !newAcc.should) {
                                        newAcc = { should: appendFilter(newAcc, $newNestedFilter) };
                                    } else if (newAcc.should) {
                                        newAcc = {
                                            ...(newAcc.customQuery && { customQuery: newAcc.customQuery }),
                                            should: appendFilter(newAcc.should, $newNestedFilter),
                                        };
                                    } else {
                                        newAcc = appendFilter(newAcc, $newNestedFilter);
                                    }
                                }
                            }
                            return newAcc;
                        }
                        if (boolQueryType === 'should') {
                            let accRef = null;
                            if (accWithAdditionalFilters) {
                                accRef = accWithAdditionalFilters[boolQueryType]
                                    ? accWithAdditionalFilters[boolQueryType] : accWithAdditionalFilters;
                            } else {
                                accRef = acc[boolQueryType] ? acc[boolQueryType] : acc;
                            }

                            // removing customQuery from accRef to avoid adding it as a filter
                            const restAccQuery: Record<string, any> = {};
                            for (const key in accRef) if (key !== CUSTOM_QUERY_KEY) restAccQuery[key] = accRef[key];
                            return {
                                [boolQueryType]: { ...restAccQuery, ...plainFilter },
                                ...(acc?.customQuery && { customQuery: acc.customQuery }),
                            };
                        }

                        if (accWithAdditionalFilters) {
                            return {
                                ...acc,
                                ...accWithAdditionalFilters,
                                should: { ...accWithAdditionalFilters.should, ...plainFilter },
                            };
                        }
                        return { ...acc, ...plainFilter };
                    }
                },
                {}
            );

        if (tabFilters.customQuery) {
            const { customQuery, should, ...restOfQuery } = tabFilters;
            if (should) {
                const shouldQuery = {
                    should: mergeElasticsearchQueries(customQuery, should),
                };
                return mergeElasticsearchQueries(shouldQuery, restOfQuery);
            }
            return mergeElasticsearchQueries(restOfQuery, customQuery);
        }
        return tabFilters;
    },

    isNested(filterDefinition: any) {
        const hasNestedFields = filterDefinition.value.indexOf('.') > -1;

        if (filterDefinition.isRawValueRequired) {
            return hasNestedFields;
        }

        return hasNestedFields && filterDefinition.value.indexOf('.raw') === -1;
    },

    getOptionsFromFilter(filter: any) {
        if (filter.items) {
            return filter.items.map((item: any): TabItem => ({
                id: item.name,
                value: item.value,
                text: tl(`grading.table.filters.${filter.name}.${item.name}`),
            }));
        }
        return [];
    },
};

export const hasFilterNonConditionalValueInConditionSelector = (filterDefinedInJson: any) => {
    return filterDefinedInJson && filterDefinedInJson.nonConditionalValuesInConditionField;
};

export const isNonConditionalValueSelectedInConditionSelector = (filterDefinedInJson: any, filterValue: any) => {
    return hasFilterNonConditionalValueInConditionSelector(filterDefinedInJson)
        && filterDefinedInJson.nonConditionalValuesInConditionField.some((item: any) => item.value === filterValue);
};

export const initializeCommonFilteringService = (appFilterService: AppSpecifiedFilterService) => {
    appSpecifiedFilterService = appFilterService;
};

export const getAppSpecifiedFilterService = (): AppSpecifiedFilterService => {
    return appSpecifiedFilterService;
};

export default commonFilteringService;
